import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react'

/** Past this the chat slides away; short of it, it springs back (design: 100 px). */
const COMMIT_PX = 100
/** A quick flick goes back from a shorter drag: px per ms, and the least distance. */
const FLICK = 0.6
const FLICK_MIN = 40
/** The slide-out before the list takes over. */
const LEAVE_MS = 200
/** Where a back drag cannot start: what the finger means to use, not the chat behind it. */
const NOT_HERE = 'input, textarea, select, button, a, [contenteditable="true"], video, audio, [role="slider"], .rc-composer-wrap, .ph-rail, .rc-media-strip'

type Drag = { id: number; x: number; y: number; on: boolean; dx: number; at: number; v: number }

/**
 * Phones: drag the open chat right, from anywhere in it, to go back to the list
 * (handoff-19 Mobile Chat Prototype). `dx` follows the finger so the page can
 * slide the chat and peek the list underneath.
 */
export function useSwipeBack(onBack: (() => void) | null) {
  const drag = useRef<Drag | null>(null)
  const [dx, setDx] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const timer = useRef<number | null>(null)
  useEffect(() => () => void (timer.current != null && window.clearTimeout(timer.current)), [])

  const end = useCallback(() => {
    const d = drag.current
    drag.current = null
    if (!d?.on) return
    const flick = d.v >= FLICK && d.dx >= FLICK_MIN
    if (onBack && (d.dx >= COMMIT_PX || flick)) {
      setLeaving(true)
      timer.current = window.setTimeout(() => {
        setLeaving(false)
        setDx(0)
        onBack()
      }, LEAVE_MS)
    } else setDx(0)
  }, [onBack])

  const handlers = {
    onPointerDown: (e: PointerEvent) => {
      if (!onBack || e.button !== 0 || (e.target as Element).closest?.(NOT_HERE)) return
      drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, on: false, dx: 0, at: e.timeStamp, v: 0 }
    },
    onPointerMove: (e: PointerEvent) => {
      const d = drag.current
      if (!d || d.id !== e.pointerId) return
      const mx = e.clientX - d.x
      const my = e.clientY - d.y
      if (!d.on) {
        if (Math.abs(my) > 10 && Math.abs(my) > Math.abs(mx)) {
          drag.current = null
          return
        }
        if (mx < 14 || mx < Math.abs(my) * 1.5) return
        d.on = true
        ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
      }
      const next = Math.max(0, mx)
      const dt = Math.max(1, e.timeStamp - d.at)
      d.v = 0.6 * ((next - d.dx) / dt) + 0.4 * d.v
      d.at = e.timeStamp
      d.dx = next
      setDx(next)
    },
    onPointerUp: end,
    onPointerCancel: () => {
      drag.current = null
      setDx(0)
    },
  }

  return { dx, leaving, dragging: dx > 0 && !leaving, handlers }
}
